/** SUNTRONIX ONE — Login (OPTION 3: premium split-screen).
 *  LEFT (~64%): enterprise-technology photograph + dark-navy overlay, official logo,
 *  brand message, 7-stage lifecycle cards (incl. REDEPLOY), real heritage line.
 *  RIGHT (~36%): email-first sign-in card (Continue → password) with real auth via
 *  POST /api/auth/login and role routing. No invented statistics. Fully responsive.
 */
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft, ShoppingCart, Box, Activity,
  ShieldCheck, RotateCcw, Send, Leaf,
} from "lucide-react";

const LOGO = "/suntronix-logo-official.webp";
const SKY = "#29ABE2";
const BG = "https://images.unsplash.com/photo-1631193816258-28b44b21e78b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

const LIFECYCLE = [
  { name: "Procure", tag: "Source smarter", icon: ShoppingCart },
  { name: "Deploy", tag: "Get to work faster", icon: Box },
  { name: "Manage", tag: "Maximize value", icon: Activity },
  { name: "Support", tag: "Keep teams moving", icon: ShieldCheck },
  { name: "Recover", tag: "Extend lifecycle", icon: RotateCcw },
  { name: "Redeploy", tag: "Instant re-assign", icon: Send },
  { name: "Recycle", tag: "A cleaner tomorrow", icon: Leaf },
];

const NAV_LINKS = ["Technology Lifecycle", "Our Solutions", "Company", "Support"];

function landingFor(role) {
  if (role === "sales_coordinator" || role === "customer_experience") return "/sales-desk";
  if (role === "customer_global_admin" || role === "customer_it_admin" || role === "customer_finance") return "/our-technology";
  if (role === "customer_employee") return "/employee-hub";
  return "/";
}

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay: d, ease: [0.22, 1, 0.36, 1] },
});

const AUTH_TIMEOUT_MS = 25000;
const transient = (e) => !e?.response || [502, 503, 504].includes(e.response.status);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function safeNext(search) {
  const n = new URLSearchParams(search).get("next");
  return n && n.startsWith("/") && !n.startsWith("//") && !n.startsWith("/login") ? n : null;
}

function loginErrorText(e) {
  const st = e?.response?.status;
  if (st === 401) return "That email or password doesn't match our records.";
  if (st === 403) return typeof e.response.data?.detail === "string" ? e.response.data.detail : "This account cannot sign in.";
  if (st === 429) return "Too many failed attempts. Please wait 15 minutes, or use Forgot password.";
  if (transient(e)) return "SUNTRONIX ONE is not responding right now (the server may be restarting). Please try again in a minute.";
  return "We couldn't sign you in right now. Please try again.";
}

export default function LoginExecutive() {
  const nav = useNavigate();
  const location = useLocation();
  const { user, refresh, signedIn } = useAuth();
  const [stage, setStage] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/public/brand-assets/support-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  const pwRef = useRef(null);
  const emailRef = useRef(null);
  const busyRef = useRef(false);
  const year = new Date().getFullYear();
  const dest = useCallback((role) => safeNext(location.search) || landingFor(role), [location.search]);

  useEffect(() => { if (user) nav(dest(user.role), { replace: true }); }, [user, nav, dest]);

  const submit = useCallback(async (e) => {
    e?.preventDefault();
    if (busyRef.current) return;  // Enter + click / repeated Enter → one request
    // Chrome password-manager autofill can fill fields without firing React onChange — read the DOM as the source of truth.
    const em = (emailRef.current?.value || email).trim().toLowerCase();
    if (!em.includes("@")) { setError("Please enter a valid work email"); return; }
    if (em !== email) setEmail(em);
    setError(null);
    if (stage === "email") {
      busyRef.current = true; setBusy(true);
      try { await api.post("/auth/tenant-hint", { email: em }, { timeout: 8000 }); } catch { /* non-enumerating, never blocks */ }
      busyRef.current = false; setBusy(false); setStage("password");
      setTimeout(() => pwRef.current?.focus(), 60);
      return;
    }
    const pw = pwRef.current?.value || password;
    if (!pw) { setError("Please enter your password"); return; }
    busyRef.current = true; setBusy(true);
    let res = null, fail = null;
    for (let i = 0; i < 3; i++) {
      try { res = await api.post("/auth/login", { email: em, password: pw }, { timeout: AUTH_TIMEOUT_MS }); fail = null; break; } catch (err) {
        fail = err;
        if (!transient(err) || i === 2) break;
        setNotice("The server is busy — retrying sign-in…");
        await sleep(2000 * (i + 1));
      }
    }
    setNotice(null);
    if (fail) {
      const msg = loginErrorText(fail);
      setError(msg); toast.error(msg);
      busyRef.current = false; setBusy(false);
      return;
    }
    setConnecting(true);
    signedIn(res.data.user);  // redirect effect navigates immediately
    refresh();  // background reconcile with /auth/me
  }, [email, password, stage, refresh, signedIn]);

  if (connecting) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#0b1220] text-white" data-testid="login-transition">
        <div className="bg-white rounded-2xl px-6 py-4 shadow-lg"><BrandLogo src={LOGO} alt="SUNTRONIX" className="w-[220px]" /></div>
        <div className="mt-6 flex items-center gap-3 text-slate-300">
          <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: SKY }} />
          Connecting your technology estate…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="login-page-container" className="min-h-screen w-full flex flex-col bg-[#0b1220]">
      {/* TOP NAV — white */}
      <header className="w-full bg-white border-b border-slate-100 px-5 sm:px-8 py-3 flex items-center justify-between" data-testid="login-topnav">
        <BrandLogo src={LOGO} alt="SUNTRONIX — GADGETS.BELOVED" className="w-[150px] sm:w-[190px]" testid="suntronix-logo-login" />
        <nav className="hidden lg:flex items-center gap-7 text-[13.5px] font-medium text-slate-600">
          {NAV_LINKS.map((l) => (l === "Support"
            ? <Link key={l} to="/help" className="hover:text-slate-900 underline-offset-4 hover:underline" data-testid="login-nav-support">{l}</Link>
            : info?.website ? <a key={l} href={info.website} target="_blank" rel="noreferrer" className="hover:text-slate-900 underline-offset-4 hover:underline" data-testid={`login-nav-${l.toLowerCase().replace(/\s+/g, "-")}`}>{l}</a>
              : <span key={l} className="text-slate-500">{l}</span>))}
        </nav>
        <div className="flex items-center gap-4 text-[13.5px]">
          <span className="hidden sm:inline text-slate-500">English</span>
          <span className="hidden sm:inline text-slate-200">|</span>
          <span className="font-semibold text-slate-900">Sign In</span>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row">
      {/* LEFT — brand / lifecycle (decorative photo + navy overlay) */}
      <div className="relative lg:w-[62%] order-2 lg:order-1 overflow-hidden flex flex-col justify-between
                      px-7 sm:px-12 lg:px-16 py-10 lg:py-12 text-white min-h-[300px]">
        <div aria-hidden className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url(${BG})` }} />
        <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(9,16,32,0.94) 0%, rgba(9,16,32,0.82) 45%, rgba(15,30,60,0.72) 100%)" }} />

        {/* message + lifecycle */}
        <div className="relative">
          <motion.div {...fade(0)} className="text-[11px] font-semibold uppercase tracking-[0.32em] text-white/70">SUNTRONIX ONE</motion.div>
          <motion.h1 {...fade(0.08)} className="mt-3 text-4xl sm:text-5xl font-bold leading-[1.06] tracking-tight text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
            A Smarter Way to<br />Manage <span style={{ color: SKY }}>Technology</span>
          </motion.h1>
          <motion.p {...fade(0.16)} className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/80">
            The complete technology lifecycle platform for modern businesses.
            <span className="block mt-1 text-white/60 text-[13.5px] tracking-wide">
              Procure · Deploy · Manage · Support · Recover · Redeploy · Recycle
            </span>
          </motion.p>

          <motion.div {...fade(0.24)} className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl" data-testid="lifecycle-cards">
            {LIFECYCLE.map(({ name, tag, icon: Icon }) => (
              <div key={name} className="rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm px-4 py-3">
                <Icon className="h-5 w-5 mb-2" style={{ color: SKY }} strokeWidth={2} />
                <div className="text-[14px] font-semibold text-white">{name}</div>
                <div className="text-[11.5px] text-white/60">{tag}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* heritage — real content only (no invented device/customer counts) */}
        <motion.div {...fade(0.34)} className="relative mt-8 lg:mt-10">
          <div className="text-[15px] font-semibold text-white/90">Pioneering Enterprise Technology Since 1999</div>
          <div className="text-[13px] text-white/60 mt-0.5">Technology. Managed around people.</div>
        </motion.div>
      </div>

      {/* RIGHT — sign-in */}
      <div className="lg:w-[38%] relative flex items-center justify-center px-5 sm:px-10 py-10 order-1 lg:order-2 bg-[#f4f7fb]">
        <motion.div {...fade(0.1)} className="w-full max-w-[430px]" data-testid="login-card-container">
          <div className="bg-white rounded-[20px] shadow-[0_20px_60px_-25px_rgba(15,23,42,0.35)] border border-slate-100 p-6 sm:p-8">
            <div className="text-center">
              <BrandLogo src={LOGO} alt="SUNTRONIX" className="mx-auto w-[200px] sm:w-[240px]" testid="suntronix-logo-login-card" />
              <div className="mt-5 text-[14px] text-slate-500 text-left">Welcome to</div>
              <div className="text-[24px] font-bold tracking-tight text-slate-900 text-left" style={{ fontFamily: "Manrope, sans-serif" }}>
                SUNTRONIX <span style={{ color: SKY }}>ONE</span>
              </div>
              <div className="text-[13px] text-slate-500 mt-1 text-left">Sign in to your account to access the technology lifecycle platform.</div>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-[13px] font-semibold text-slate-700 mb-1.5">Email address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input id="login-email" ref={emailRef} name="username" type="email" autoComplete="username" value={email} readOnly={stage === "password"}
                    onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" data-testid="login-email"
                    className="w-full h-12 pl-10 pr-3 rounded-xl border border-slate-300 text-[14px] transition-all
                               focus:outline-none focus:border-[#29ABE2] focus:ring-4 focus:ring-[#29ABE2]/20
                               read-only:bg-slate-50 read-only:text-slate-500" />
                </div>
              </div>

              <AnimatePresence>
                {stage === "password" && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.28 }}>
                    <label htmlFor="login-password" className="block text-[13px] font-semibold text-slate-700 mb-1.5">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input id="login-password" ref={pwRef} name="password" type={showPw ? "text" : "password"} autoComplete="current-password" value={password}
                        onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" data-testid="login-password"
                        className="w-full h-12 pl-10 pr-10 rounded-xl border border-slate-300 text-[14px] transition-all
                                   focus:outline-none focus:border-[#29ABE2] focus:ring-4 focus:ring-[#29ABE2]/20" />
                      <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="login-password-toggle">
                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <div className="mt-2.5 flex items-center justify-between">
                      <label className="inline-flex items-center gap-2 text-[12.5px] text-slate-600 cursor-pointer" data-testid="login-remember-checkbox">
                        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-[#29ABE2] focus:ring-[#29ABE2]/30" />
                        Keep me signed in
                      </label>
                      <Link to="/forgot-password"
                        className="text-[12.5px] font-medium text-[#29ABE2] hover:underline" data-testid="login-forgot">Forgot password?</Link>
                    </div>
                    <button type="button" onClick={() => { setStage("email"); setPassword(""); setError(null); }} data-testid="login-back"
                      className="mt-2 inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-[#29ABE2]">
                      <ArrowLeft className="h-3 w-3" /> Use a different email
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700" data-testid="login-error">{error}</div>}
              {notice && <div role="status" className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12.5px] text-sky-800" data-testid="login-notice">{notice}</div>}
              <button type="submit" disabled={busy} data-testid="login-submit"
                className="w-full h-12 rounded-xl text-white font-semibold text-[14.5px] inline-flex items-center justify-center gap-2
                           transition-all disabled:opacity-60 shadow-lg shadow-[#29ABE2]/25 hover:brightness-105"
                style={{ background: SKY }}>
                {busy ? "Please wait…" : stage === "email" ? "Continue" : "Sign in"}
                {!busy && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-center gap-4 text-[12px] text-slate-400">
              {info?.privacy_url ? <a href={info.privacy_url} {...(info.privacy_url.startsWith("/") ? {} : { target: "_blank", rel: "noreferrer" })} className="text-slate-600 hover:text-slate-900" data-testid="login-privacy">Privacy</a> : <span className="text-slate-500" title="Policy not yet published" data-testid="login-privacy">Privacy (not yet published)</span>}
              <span className="text-slate-200">·</span>
              {info?.terms_url ? <a href={info.terms_url} {...(info.terms_url.startsWith("/") ? {} : { target: "_blank", rel: "noreferrer" })} className="text-slate-600 hover:text-slate-900" data-testid="login-terms">Terms</a> : <span className="text-slate-500" title="Terms not yet published" data-testid="login-terms">Terms (not yet published)</span>}
              <span className="text-slate-200">·</span>
              <Link to="/help" className="text-slate-600 hover:text-slate-900" data-testid="login-support">Support</Link>
            </div>
            <div className="mt-2 text-center text-[11.5px] text-slate-400" data-testid="login-copyright">© {year} SUNTRONIX</div>
          </div>
        </motion.div>
      </div>
      </div>
    </div>
  );
}
